/**
 * Hook to manage avatar walking animation
 * Handles step-by-step movement along a path with rover following
 * Rover uses physics-based trailing with inertia and leash pull
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { findHexPath, hexEqual, normalizeHexCoord, type HexCoord } from '@/utils/hexPathfinding';
import { playSound } from '@/audio/rushAudio';

interface WalkingState {
  isWalking: boolean;
  currentPath: HexCoord[];
  currentStep: number;
  avatarPosition: HexCoord;
  roverPosition: HexCoord;
  previousAvatarPosition: HexCoord; // Track for direction calculation
}

interface UseAvatarWalkingOptions {
  initialAvatarPosition: HexCoord;
  initialRoverPosition: HexCoord;
  stepDuration?: number; // milliseconds per step
  onWalkComplete?: (finalPosition: HexCoord) => void;
}

/**
 * Calculate which hex is "behind" the avatar based on movement direction
 */
function calculateRoverFollowPosition(
  currentAvatar: HexCoord,
  previousAvatar: HexCoord,
  pathIndex: number,
  path: HexCoord[]
): HexCoord {
  // If we have a previous position in the path, rover should be there
  if (pathIndex > 0 && path[pathIndex - 1]) {
    return path[pathIndex - 1];
  }
  
  // Otherwise, calculate opposite direction from movement
  const dq = currentAvatar.q - previousAvatar.q;
  const dr = currentAvatar.r - previousAvatar.r;
  const ds = currentAvatar.s - previousAvatar.s;
  
  // Rover should be in opposite direction (behind)
  return {
    q: currentAvatar.q - dq,
    r: currentAvatar.r - dr,
    s: currentAvatar.s - ds,
  };
}

export function useAvatarWalking({
  initialAvatarPosition,
  initialRoverPosition,
  stepDuration = 300,
  onWalkComplete,
}: UseAvatarWalkingOptions) {
  const [state, setState] = useState<WalkingState>({
    isWalking: false,
    currentPath: [],
    currentStep: 0,
    avatarPosition: initialAvatarPosition,
    roverPosition: initialRoverPosition,
    previousAvatarPosition: initialAvatarPosition,
  });
  
  const stepTimerRef = useRef<NodeJS.Timeout | null>(null);
  const walkCompleteCallbackRef = useRef(onWalkComplete);
  const lastSyncedPositionRef = useRef<HexCoord | null>(null);
  
  // Update callback ref
  useEffect(() => {
    walkCompleteCallbackRef.current = onWalkComplete;
  }, [onWalkComplete]);
  
  // Sync position when game state changes (e.g., after phase transitions)
  // Only sync if we're not walking and the position has actually changed
  useEffect(() => {
    if (!state.isWalking && initialAvatarPosition) {
      // Normalize coordinates to ensure s is always defined
      const normalizedInitial = normalizeHexCoord(initialAvatarPosition);
      const normalizedRover = normalizeHexCoord(initialRoverPosition);
      
      const lastSynced = lastSyncedPositionRef.current;
      const needsSync = !lastSynced || !hexEqual(lastSynced, normalizedInitial);
      
      // Also check if current state differs from initial
      const stateDiffers = !hexEqual(state.avatarPosition, normalizedInitial);
      
      if (needsSync && stateDiffers) {
        console.log('🔄 Syncing avatar position from game state:', normalizedInitial);
        lastSyncedPositionRef.current = normalizedInitial;
        setState(prev => ({
          ...prev,
          avatarPosition: normalizedInitial,
          previousAvatarPosition: normalizedInitial,
          roverPosition: normalizedRover,
        }));
      }
    }
  }, [initialAvatarPosition, initialRoverPosition, state.isWalking]);
  
  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (stepTimerRef.current) {
        clearTimeout(stepTimerRef.current);
      }
    };
  }, []);
  
  /**
   * Start walking to a destination
   */
  const walkTo = useCallback((destination: HexCoord | { q: number; r: number; s?: number }) => {
    // Normalize destination to ensure s is defined
    const normalizedDest = normalizeHexCoord(destination);
    console.log('🚶 Starting walk from', state.avatarPosition, 'to', normalizedDest);
    
    // Calculate path (findHexPath also normalizes internally)
    const path = findHexPath(state.avatarPosition, normalizedDest);
    console.log('🗺️  Path calculated:', path);
    
    if (path.length <= 1) {
      console.log('⚠️  Already at destination or invalid path');
      return;
    }
    
    // Start walking
    setState(prev => ({
      ...prev,
      isWalking: true,
      currentPath: path,
      currentStep: 0,
      previousAvatarPosition: prev.avatarPosition,
    }));
  }, [state.avatarPosition]);
  
  /**
   * Advance to next step in path
   */
  useEffect(() => {
    if (!state.isWalking || state.currentPath.length === 0) {
      return;
    }
    
    const nextStepIndex = state.currentStep + 1;
    
    // Check if we've reached the end
    if (nextStepIndex >= state.currentPath.length) {
      console.log('✅ Walk complete!');
      console.log('🤖 Rover now targeting avatar position, will catch up smoothly');
      setState(prev => ({
        ...prev,
        isWalking: false,
        currentPath: [],
        currentStep: 0,
        // Update rover position to target the avatar's final position
        roverPosition: prev.avatarPosition,
      }));
      
      // Call completion callback with avatar's final position
      if (walkCompleteCallbackRef.current) {
        walkCompleteCallbackRef.current(state.avatarPosition);
      }
      return;
    }
    
    // Schedule next step
    stepTimerRef.current = setTimeout(() => {
      const nextAvatarPos = state.currentPath[nextStepIndex];
      
      // Calculate rover position (behind avatar based on movement direction)
      const nextRoverPos = calculateRoverFollowPosition(
        nextAvatarPos,
        state.avatarPosition,
        nextStepIndex,
        state.currentPath
      );
      
      console.log(`🦶 Step ${nextStepIndex}/${state.currentPath.length - 1}: Avatar to`, nextAvatarPos, 'Rover to', nextRoverPos);
      playSound('step');
      
      setState(prev => ({
        ...prev,
        currentStep: nextStepIndex,
        previousAvatarPosition: prev.avatarPosition,
        avatarPosition: nextAvatarPos,
        roverPosition: nextRoverPos,
      }));
    }, stepDuration);
    
    return () => {
      if (stepTimerRef.current) {
        clearTimeout(stepTimerRef.current);
      }
    };
  }, [state.isWalking, state.currentStep, state.currentPath, state.avatarPosition, stepDuration]);
  
  /**
   * Cancel current walk
   */
  const cancelWalk = useCallback(() => {
    console.log('🛑 Walk cancelled');
    if (stepTimerRef.current) {
      clearTimeout(stepTimerRef.current);
    }
    setState(prev => ({
      ...prev,
      isWalking: false,
      currentPath: [],
      currentStep: 0,
    }));
  }, []);
  
  return {
    isWalking: state.isWalking,
    avatarPosition: state.avatarPosition,
    roverPosition: state.roverPosition,
    currentPath: state.currentPath,
    walkTo,
    cancelWalk,
  };
}


